// src/pages/Exams/ExamRegistrationEdit.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { supabase } from '../../supabaseClient';
import { useSubjects } from '../../hooks/useSubjects';
import { useExamSessions } from '../../hooks/useExamSessions';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const PARENT_TABLE = 'student_exam_session';
const CHILD_TABLE = 'student_exam_info';

const PARENT_STATUS = ['Registered', 'Deferred', 'Cancelled'];
const CHILD_REG_STATUS = ['Registered', 'Deferred', 'Cancelled'];
const CHILD_RES_STATUS = ['Pending', 'Passed', 'Failed', 'Absent', 'Withheld'];

const currency = (n) =>
  `RM${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ExamRegistrationEdit() {
  const { id } = useParams(); // parent id (session_registration_id)
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromStudent = searchParams.get('fromStudent'); // student id if navigated from Student details

  const { subjects = [], isLoading: subjectsLoading } = useSubjects?.() ?? {
    subjects: [],
    isLoading: false,
  };
  const { examSessions = [], loading: sessionsLoading } = useExamSessions?.() ?? {
    examSessions: [],
    loading: false,
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Parent
  const [parent, setParent] = useState(null);

  // Children working copy (non-deleted only)
  const [rows, setRows] = useState([]);

  // For adding new subject
  const [addSubjectId, setAddSubjectId] = useState('');

  // Detect if user came from any /students/:id page (fallback when no ?fromStudent present)
  const [cameFromStudents, setCameFromStudents] = useState(false);
  useEffect(() => {
    try {
      const ref = document.referrer || '';
      const origin = window.location.origin;
      if (ref.startsWith(origin)) {
        const pathname = new URL(ref).pathname;
        if (/^\/students\/[^/]+/.test(pathname)) setCameFromStudents(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const parentStudentId = parent?.student_id;

  // Back target + label (context-aware)
  const backTarget = fromStudent ? `/students/${fromStudent}?tab=exams` : '/exam-registrations';
  const backLabel = fromStudent ? 'Back to Student Exams' : 'Back to Exam Registrations';

  // Load parent + children
  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr('');
    try {
      const { data: p, error: pErr } = await supabase
        .from(PARENT_TABLE)
        .select(
          `
          id, student_id, exam_session_id, registration_status,
          venue, total_fee_paid, payment_date, remarks,
          is_deleted, created_at, updated_at,
          students:students ( id, full_name ),
          exam_sessions:exam_sessions ( id, name, start_date, end_date )
        `
        )
        .eq('id', id)
        .eq('is_deleted', false)
        .single();
      if (pErr) throw pErr;

      const { data: kids, error: cErr } = await supabase
        .from(CHILD_TABLE)
        .select(
          `
          id, session_registration_id, student_id, subject_id, attempt_no,
          registration_status, result_status, notes, is_deleted,
          subjects:subjects ( id, code, name )
        `
        )
        .eq('session_registration_id', id)
        .eq('is_deleted', false)
        .order('subject_id', { ascending: true });
      if (cErr) throw cErr;

      const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
      const mapped = (kids || [])
        .map((r) => ({
          id: r.id,
          subject_id: r.subject_id,
          subject_code: r.subjects?.code || '',
          subject_name: r.subjects?.name || '',
          attempt_no: r.attempt_no || 1,
          registration_status: r.registration_status || 'Registered',
          result_status: r.result_status || 'Pending',
          notes: r.notes || '',
          _dirty: false,
          _new: false,
          _deleted: false,
        }))
        .sort((a, b) => {
          const anum = parseInt((a.subject_code || '').replace(/\D+/g, ''), 10);
          const bnum = parseInt((b.subject_code || '').replace(/\D+/g, ''), 10);
          if (!Number.isNaN(anum) && !Number.isNaN(bnum) && anum !== bnum) {
            return anum - bnum;
          }
          return collator.compare((a.subject_code || '').trim(), (b.subject_code || '').trim());
        });

      setParent({
        id: p.id,
        student_id: p.student_id,
        student_name: p.students?.full_name || '—',
        exam_session_id: p.exam_session_id,          // keep id for editing
        exam_session_name: p.exam_sessions?.name || '—',
        registration_status: p.registration_status,
        venue: p.venue || '',
        total_fee_paid: p.total_fee_paid ?? '',
        payment_date: p.payment_date ?? '',
        remarks: p.remarks || '',
      });
      setRows(mapped);
    } catch (e) {
      console.error(e);
      setErr(e?.message || 'Failed to load registration.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const subjectMap = useMemo(() => {
    const m = new Map();
    for (const s of subjects || []) m.set(String(s.id), s);
    return m;
  }, [subjects]);

  const existingSubjectIds = useMemo(
    () => new Set(rows.filter((r) => !r._deleted).map((r) => String(r.subject_id))),
    [rows]
  );

  const availableSubjects = useMemo(() => {
    const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
    return (subjects || [])
      .filter((s) => !existingSubjectIds.has(String(s.id)))
      .sort((a, b) => {
        const ac = (a.code || '').trim();
        const bc = (b.code || '').trim();
        const anum = parseInt(ac.replace(/\D+/g, ''), 10);
        const bnum = parseInt(bc.replace(/\D+/g, ''), 10);
        if (!Number.isNaN(anum) && !Number.isNaN(bnum) && anum !== bnum) {
          return anum - bnum;
        }
        return collator.compare(ac, bc);
      });
  }, [subjects, existingSubjectIds]);

  const markDirty = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, _dirty: true };
      return next;
    });
  };

  const softRemoveRow = (idx) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], _deleted: true, _dirty: true };
      return next;
    });
  };

  const fetchNextAttemptNos = useCallback(
    async (studentId, subjectIds) => {
      if (!subjectIds.length) return {};
      const { data, error } = await supabase
        .from(CHILD_TABLE)
        .select('subject_id, attempt_no')
        .eq('student_id', studentId)
        .eq('is_deleted', false)
        .in('subject_id', subjectIds);
      if (error) throw error;

      const maxBySubject = {};
      for (const r of data || []) {
        const sid = String(r.subject_id);
        maxBySubject[sid] = Math.max(Number(maxBySubject[sid] || 0), Number(r.attempt_no || 0));
      }
      const out = {};
      for (const sid of subjectIds) {
        out[String(sid)] = (maxBySubject[String(sid)] || 0) + 1;
      }
      return out;
    },
    []
  );

  const handleAddSubject = async () => {
    if (!addSubjectId) return;
    const sid = String(addSubjectId);
    if (existingSubjectIds.has(sid)) {
      setAddSubjectId('');
      return;
    }
    try {
      const nextNo = await fetchNextAttemptNos(parentStudentId, [sid]);
      const subj = subjectMap.get(sid);
      setRows((prev) => [
        ...prev,
        {
          id: undefined,
          subject_id: sid,
          subject_code: subj?.code || '',
          subject_name: subj?.name || '',
          attempt_no: nextNo[sid] || 1,
          registration_status: 'Registered',
          result_status: 'Pending',
          notes: '',
          _dirty: true,
          _new: true,
          _deleted: false,
        },
      ]);
      setAddSubjectId('');
    } catch (e) {
      alert(e?.message || 'Failed to add subject.');
    }
  };

  // Parent guards: when parent is deferred/cancelled → force child result_status=pending
  useEffect(() => {
    if (!parent) return;
    if (parent.registration_status === 'Deferred' || parent.registration_status === 'Cancelled') {
      setRows((prev) =>
        prev.map((r) =>
          r._deleted
            ? r
            : {
                ...r,
                result_status: 'Pending',
                _dirty: true,
              }
        )
      );
    }
  }, [parent?.registration_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!parent) return;
    setSaving(true);
    setErr('');

    try {
      // 0) Validate and duplicate check for (student, exam_session)
      if (!parent.exam_session_id) {
        throw new Error('Please select an exam session.');
      }
      {
        const { data: dup, error: dupErr } = await supabase
          .from('student_exam_session')
          .select('id')
          .eq('student_id', parent.student_id)
          .eq('exam_session_id', parent.exam_session_id)
          .eq('is_deleted', false)
          .neq('id', parent.id);
        if (dupErr) throw dupErr;
        if (dup && dup.length > 0) {
          throw new Error('This student already has a registration for the selected exam session.');
        }
      }

      // 1) Update parent
      const parentPayload = {
        exam_session_id: parent.exam_session_id,
        registration_status: parent.registration_status,
        venue: parent.venue || null,
        total_fee_paid:
          parent.total_fee_paid === '' || parent.total_fee_paid == null
            ? null
            : Number(parent.total_fee_paid),
        payment_date: parent.payment_date || null,
        remarks: parent.remarks || null,
      };
      const { error: pErr } = await supabase.from(PARENT_TABLE).update(parentPayload).eq('id', parent.id);
      if (pErr) throw pErr;

      // 2) Apply children changes
      const toSoftDelete = rows.filter((r) => r._deleted && r.id);
      const toInsert = rows.filter((r) => r._new && !r._deleted);
      const toUpdate = rows.filter((r) => !r._new && r._dirty && !r._deleted);

      if (toSoftDelete.length > 0) {
        const { error: dErr } = await supabase
          .from(CHILD_TABLE)
          .update({ is_deleted: true })
          .in('id', toSoftDelete.map((r) => r.id));
        if (dErr) throw dErr;
      }

      if (toInsert.length > 0) {
        const payload = toInsert.map((r) => ({
          session_registration_id: parent.id,
          student_id: parent.student_id,
          subject_id: r.subject_id,
          attempt_no: r.attempt_no,
          registration_status: r.registration_status,
          result_status: parent.registration_status === 'Registered' ? r.result_status : 'Pending',
          notes: r.notes || null,
          is_deleted: false,
        }));
        const { error: iErr } = await supabase.from(CHILD_TABLE).insert(payload);
        if (iErr) throw iErr;
      }

      if (toUpdate.length > 0) {
        const forcePending = parent.registration_status !== 'Registered';
        for (const r of toUpdate) {
          const patch = {
            registration_status: r.registration_status,
            result_status: forcePending ? 'Pending' : r.result_status,
            notes: r.notes || null,
          };
          const { error: uErr } = await supabase.from(CHILD_TABLE).update(patch).eq('id', r.id);
          if (uErr) throw uErr;
        }
      }

      alert('Registration updated.');
      if (fromStudent) {
        navigate(`/students/${fromStudent}?tab=exams`, { replace: true });
      } else if (cameFromStudents) {
        navigate(-1); // back to student
      } else {
        navigate('/exam-registrations', { replace: true });
      }
    } catch (e) {
      console.error(e);
      setErr(e?.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (fromStudent) {
      navigate(`/students/${fromStudent}?tab=exams`, { replace: true });
    } else if (cameFromStudents) {
      navigate(-1);
    } else {
      navigate('/exam-registrations', { replace: true });
    }
  };

  if (loading) return <div className="text-center py-8">Loading registration…</div>;
  if (err && !parent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleCancel} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            {backLabel}
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Exam Registration</h1>
        </div>
        <Card>
          <div className="py-10 text-center text-red-600">{err}</div>
        </Card>
      </div>
    );
  }
  if (!parent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleCancel} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            {backLabel}
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Exam Registration</h1>
        </div>
        <Card>
          <div className="py-10 text-center text-gray-600">Registration not found.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={handleCancel} className="flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          {backLabel}
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Exam Registration</h1>
      </div>

      {err && <div className="p-3 rounded bg-red-100 text-red-700">{err}</div>}

      {/* Parent info */}
      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
            <div className="px-3 py-2 bg-gray-50 rounded border border-gray-200">
              {parent.student_name}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exam Session</label>
            <select
              value={parent.exam_session_id || ''}
              onChange={(e) => {
                const newId = e.target.value || null;
                const newName =
                  (examSessions.find((s) => String(s.id) === String(newId))?.name) ||
                  parent.exam_session_name;
                setParent((p) => ({ ...p, exam_session_id: newId, exam_session_name: newName }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              disabled={sessionsLoading || saving}
            >
              <option value="">Select a session…</option>
              {examSessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration Status</label>
            <select
              value={parent.registration_status}
              onChange={(e) => setParent((p) => ({ ...p, registration_status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              disabled={saving}
            >
              {PARENT_STATUS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Fee Paid</label>
            <Input
              type="number"
              step="0.01"
              value={parent.total_fee_paid ?? ''}
              onChange={(e) => setParent((p) => ({ ...p, total_fee_paid: e.target.value }))}
              disabled={saving}
              placeholder="e.g., 0.00"
            />
            {parent.total_fee_paid ? (
              <div className="mt-1 text-xs text-gray-500">{currency(parent.total_fee_paid)}</div>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
            <Input
              type="date"
              value={parent.payment_date ?? ''}
              onChange={(e) => setParent((p) => ({ ...p, payment_date: e.target.value }))}
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
            <Input
              value={parent.venue || ''}
              onChange={(e) => setParent((p) => ({ ...p, venue: e.target.value }))}
              disabled={saving}
              placeholder="e.g., KL, SABAH"
            />
          </div>

          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              value={parent.remarks || ''}
              onChange={(e) => setParent((p) => ({ ...p, remarks: e.target.value }))}
              rows={3}
              disabled={saving}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Optional remarks…"
            />
          </div>
        </div>
      </Card>

      {/* Children */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Subjects in this Registration</h2>
          <div className="flex gap-2">
            <select
              value={addSubjectId}
              onChange={(e) => setAddSubjectId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
              disabled={subjectsLoading || saving}
            >
              <option value="">Add subject…</option>
              {availableSubjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.code ? `${s.code} — ` : '') + (s.name || '')}
                </option>
              ))}
            </select>
            <Button onClick={handleAddSubject} disabled={!addSubjectId || saving}>
              Add
            </Button>
          </div>
        </div>

        {rows.filter((r) => !r._deleted).length === 0 ? (
          <div className="py-6 text-gray-600">No subjects in this registration.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Subject</th>
                  <th className="px-4 py-2 text-left">Attempt #</th>
                  <th className="px-4 py-2 text-left">Reg. Status</th>
                  <th className="px-4 py-2 text-left">Result</th>
                  <th className="px-4 py-2 text-left">Notes</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r, idx) =>
                  r._deleted ? null : (
                    <tr key={r.id || `new-${r.subject_id}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">
                          {r.subject_code ? `${r.subject_code} — ${r.subject_name}` : r.subject_name}
                        </div>
                        {r._new && <div className="text-xs text-indigo-600">New</div>}
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min={1}
                          value={r.attempt_no}
                          onChange={(e) =>
                            markDirty(idx, { attempt_no: Math.max(1, Number(e.target.value || 1)) })
                          }
                          disabled={saving}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={r.registration_status}
                          onChange={(e) => markDirty(idx, { registration_status: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded-md"
                          disabled={saving}
                        >
                          {CHILD_REG_STATUS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={parent.registration_status === 'Registered' ? r.result_status : 'Pending'}
                          onChange={(e) => markDirty(idx, { result_status: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded-md"
                          disabled={saving || parent.registration_status !== 'Registered'}
                        >
                          {CHILD_RES_STATUS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        {parent.registration_status !== 'Registered' && (
                          <div className="text-xs text-gray-500 mt-1">Locked while parent not Registered</div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          value={r.notes || ''}
                          onChange={(e) => markDirty(idx, { notes: e.target.value })}
                          disabled={saving}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => softRemoveRow(idx)}
                          disabled={saving}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

export default ExamRegistrationEdit;
